import React, { useState } from 'react';
import { Button, Form, Modal } from '@/components/ui';
import { Vehicle } from '../../../types/Vehicle';
import vehicleService, { fieldErrorsOf } from '../../../services/vehicleService';
import { todayPacific } from '../../../utils/wrapDate';
import { formatServiceArea } from '@/components/ui';

interface EquipmentBulkBarProps {
  selected: Vehicle[];
  foremanAreas: { id: number; name: string; serviceAreaId?: number | null; serviceArea?: { name: string; code?: string | null } | null }[];
  canSetAvailability: boolean;
  canTransferForemanArea: boolean;
  onClear: () => void;
  onApplied: () => Promise<void>;
}

const EquipmentBulkBar: React.FC<EquipmentBulkBarProps> = ({
  selected,
  foremanAreas,
  canSetAvailability,
  canTransferForemanArea,
  onClear,
  onApplied
}) => {
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [availability, setAvailability] = useState('InService');
  const [reason, setReason] = useState('');
  const [estimatedReturn, setEstimatedReturn] = useState('');
  const [foremanAreaId, setForemanAreaId] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const ids = selected.map((unit) => unit.id);
  const currentAreaNames = Array.from(new Set(selected.map((u) => u.foremanArea?.name).filter(Boolean)));
  // V3-056: only foreman areas in every selected unit's service area; other moves are transfers.
  const moveTargets = foremanAreas.filter((area) =>
    selected.every((unit) => {
      const unitAreas = (unit.vehicleServiceAreas ?? []).map((sa) => sa.serviceAreaId ?? sa.serviceArea?.id);
      return unitAreas.length === 0 || (area.serviceAreaId != null && unitAreas.includes(area.serviceAreaId));
    })
  );

  const run = async (fn: () => Promise<unknown>) => {
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      await fn();
      setAvailabilityOpen(false);
      setTransferOpen(false);
      setReason('');
      setNote('');
      onClear();
      await onApplied();
    } catch (err) {
      const fields = fieldErrorsOf(err);
      setFieldErrors(fields);
      // Reason / return date errors show under their fields; anything else above the form.
      const shownUnderField = fields.reason || fields.estimatedReturnToServiceDate;
      setError(fields._form ?? (shownUnderField ? null : Object.values(fields)[0] ?? null));
    } finally {
      setSaving(false);
    }
  };

  if (selected.length === 0) return null;

  return (
    <>
      <div className="tw-mb-3 tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-border tw-border-line-input tw-bg-surface-alt tw-px-4 tw-py-2.5 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between">
        <span className="tw-text-sm tw-font-medium tw-text-content">
          {selected.length} {selected.length === 1 ? 'unit' : 'units'} selected
        </span>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-2">
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={!canSetAvailability}
            title={canSetAvailability ? undefined : 'Requires the Set Availability permission'}
            onClick={() => {
              setError(null);
              setAvailabilityOpen(true);
            }}
          >
            Set Availability
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            disabled={!canTransferForemanArea}
            title={canTransferForemanArea ? undefined : 'Requires the Transfer Foreman Area permission'}
            onClick={() => {
              setError(null);
              setTransferOpen(true);
            }}
          >
            Transfer Foreman Area
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={onClear}>
            Clear Selection
          </Button>
        </div>
      </div>

      <Modal show={availabilityOpen} onHide={() => setAvailabilityOpen(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Set Availability</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <p className="tw-mb-2 tw-text-sm tw-text-danger">{error}</p>}
          <Form.Label>Availability</Form.Label>
          <Form.Select value={availability} onChange={(e) => setAvailability(e.target.value)}>
            <option value="InService">In Service</option>
            <option value="OutOfService">Out of Service</option>
          </Form.Select>
          {availability === 'OutOfService' && (
            <>
              <Form.Label className="tw-mt-3">Reason <span className="tw-text-danger">*</span></Form.Label>
              <Form.Control
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                isInvalid={!!fieldErrors.reason}
                aria-describedby={fieldErrors.reason ? 'bulk-reason-error' : undefined}
              />
              {fieldErrors.reason && <div id="bulk-reason-error" className="tw-mt-1 tw-text-sm tw-text-danger">{fieldErrors.reason}</div>}
              <Form.Label className="tw-mt-3">Estimated Return (Optional)</Form.Label>
              <Form.Control
                type="date"
                min={todayPacific()}
                value={estimatedReturn}
                onChange={(e) => setEstimatedReturn(e.target.value)}
                isInvalid={!!fieldErrors.estimatedReturnToServiceDate}
              />
              {fieldErrors.estimatedReturnToServiceDate && (
                <div className="tw-mt-1 tw-text-sm tw-text-danger">{fieldErrors.estimatedReturnToServiceDate}</div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAvailabilityOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving}
            onClick={() => {
              // FORM-01: say what's missing under the field instead of disabling Apply.
              const errors: Record<string, string> = {};
              if (availability === 'OutOfService' && !reason.trim()) errors.reason = 'Reason required';
              if (availability === 'OutOfService' && estimatedReturn && estimatedReturn < todayPacific()) {
                errors.estimatedReturnToServiceDate = "Estimated return can't be before today";
              }
              setFieldErrors(errors);
              if (Object.keys(errors).length > 0) return;
              run(() => vehicleService.bulkAvailability(ids, {
              availability,
              reason: reason.trim() || undefined,
              estimatedReturnToServiceDate: availability === 'OutOfService' ? estimatedReturn || null : null
            }));
            }}
          >
            {saving ? 'Saving…' : 'Apply'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={transferOpen} onHide={() => setTransferOpen(false)} size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Transfer Foreman Area</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <p className="tw-mb-2 tw-text-sm tw-text-danger">{error}</p>}
          <p className="tw-mb-2 tw-text-sm tw-text-content-secondary">
            Current: {currentAreaNames.length ? currentAreaNames.join(', ') : 'Unassigned'}
          </p>
          <Form.Label>Destination</Form.Label>
          <Form.Select value={foremanAreaId} onChange={(e) => setForemanAreaId(e.target.value)}>
            <option value="">Select a Foreman Area</option>
            {moveTargets.map((area) => {
              // Two areas share a name ("Quality Assurance"): say which service area each is in.
              const sa = area.serviceArea ? formatServiceArea(area.serviceArea, true) : null;
              return (
                <option key={area.id} value={area.id.toString()}>{sa ? `${area.name} (${sa})` : area.name}</option>
              );
            })}
          </Form.Select>
          {moveTargets.length === 0 && (
            <p className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">
              The selected units are in different service areas. Move them one service area at a time.
            </p>
          )}
          <Form.Label className="tw-mt-3">Note (Optional)</Form.Label>
          <Form.Control value={note} onChange={(e) => setNote(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTransferOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={saving || !foremanAreaId}
            onClick={() => run(() => vehicleService.bulkTransferForemanArea(ids, {
              foremanAreaId: parseInt(foremanAreaId, 10),
              note: note.trim() || undefined
            }))}
          >
            {saving ? 'Saving…' : 'Transfer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EquipmentBulkBar;
